import { Link } from 'react-router-dom';
import { ArrowRight, Package, Truck, Clock, Shield, Award, Users } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

export default function HomePage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-8rem)] flex items-center justify-center overflow-hidden pb-20">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-hero z-10" />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1769701000480-6c3d8ab912c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxsb2dpc3RpY3MlMjBkZWxpdmVyeSUyMHRydWNrc3xlbnwwfHx8fDE3NzgxMDM5OTV8MA&ixlib=rb-4.1.0&q=85)'
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              <Badge variant="outline" className="mb-6 border-primary/50 text-primary bg-primary/10 animate-fade-in">
                Trusted Logistics Partner in GTA & Ontario
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-slide-up">
                <span className="text-gradient-silver">Fast & Reliable</span>
                <br />
                <span className="text-gradient">Courier & LTL Services</span>
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto lg:mx-0 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                Professional freight and courier solutions tailored for businesses across the Greater Toronto Area and Ontario. 
                Same-day delivery, LTL shipments, and dedicated transport - all handled with precision and care.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-8 justify-center lg:justify-start animate-slide-up" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Truck className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">500+</div>
                    <div className="text-sm text-muted-foreground">Deliveries/Month</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">99.5%</div>
                    <div className="text-sm text-muted-foreground">On-Time Rate</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-muted-foreground">Support</div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-slide-up" style={{ animationDelay: '0.3s' }}>
                <Link to="/quote">
                  <Button 
                    size="lg" 
                    className="bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 text-lg px-8 group w-full sm:w-auto"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/track">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-primary/50 text-primary hover:bg-primary/10 text-lg px-8 w-full sm:w-auto"
                  >
                    Track Shipment
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right content - Hero Image */}
            <div className="relative lg:block hidden animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden shadow-glow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1769701000480-6c3d8ab912c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxsb2dpc3RpY3MlMjBkZWxpdmVyeSUyMHRydWNrc3xlbnwwfHx8fDE3NzgxMDM5OTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Syncline Logistics Operations"
                  className="w-full h-auto rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-card border border-primary/30 rounded-2xl p-6 shadow-glow animate-float">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gradient-primary">
                    <Truck className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gradient">15+ Years</div>
                    <div className="text-sm text-muted-foreground">Industry Experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Services Preview */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 scroll-animate">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Our Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Complete <span className="text-gradient">Logistics Solutions</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From same-day courier to full freight transport, we provide comprehensive delivery services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Truck, title: 'LTL Shipments', desc: 'Cost-effective freight solutions' },
              { icon: Clock, title: 'Same-Day Courier', desc: 'Urgent deliveries handled fast' },
              { icon: Package, title: 'Scheduled Delivery', desc: 'Flexible timing options' },
              { icon: Shield, title: 'Freight Transport', desc: 'Full truckload services' }
            ].map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-3 rounded-xl bg-gradient-primary inline-flex mb-4 group-hover:shadow-glow transition-all duration-300">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {service.desc}
                  </p>
                </Card>
              );
            })}
          </div>

          <div className="text-center scroll-animate">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Preview */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 blur-[120px] rounded-full" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 scroll-animate">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Why Choose Us
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Trusted by <span className="text-gradient">Ontario Businesses</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, stat: '$2M', label: 'Coverage', title: 'Fully Insured' },
              { icon: Clock, stat: '99.5%', label: 'On-Time', title: 'Guaranteed' },
              { icon: Award, stat: '15+', label: 'Years', title: 'Experience' },
              { icon: Users, stat: '24/7', label: 'Support', title: 'Dedicated' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={index} 
                  className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow text-center"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-3 rounded-xl bg-gradient-primary inline-flex mb-4">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div className="text-3xl font-bold text-gradient mb-1">{item.stat}</div>
                  <div className="text-sm text-muted-foreground mb-2">{item.label}</div>
                  <div className="font-semibold">{item.title}</div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12 scroll-animate">
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
                Learn More About Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/5 blur-[150px] rounded-full" />
        
        <div className="container mx-auto px-4 relative z-10">
          <Card className="scroll-animate p-12 bg-gradient-card backdrop-blur-sm border-primary/20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Ship? <span className="text-gradient">Get Started Today</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Request a free quote and experience the Syncline Logistics difference. Fast, reliable, and professional service guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button size="lg" className="bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 text-lg px-8 w-full sm:w-auto">
                  Get a Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/track">
                <Button size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 text-lg px-8 w-full sm:w-auto">
                  Track Shipment
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
